import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useMutation, useLazyQuery } from '@apollo/client';
import { Alert } from 'antd';
import { CREATE_ASSET, CREATE_SCORM_COURSE, NOTIFY_SCORM_COURSE } from '../../../gql/mutations';
import { UPLOAD_STATUS_QUERY } from '../../../gql/queries';
import AssetPreview from './assetPreview/AssetPreview';
import UploadButton from './uploadButton/UploadButton'; 
import Loading from '../../loading/Loading';
import { useAuthState } from '../../../auth/useAuthState';

const AssetUpload = ( { value = [], onChange, accept, type, maxAssets = 1 } ) => {
    
    const { hasPermission } = useAuthState();
    const [ largeFileLoading, setLargeFileLoading ] = useState( false  );
    const canCreate = hasPermission( 'create-asset' );
    const assetList = value?.filter( asset => asset ) || [];
    const [ createAsset, { loading: createAssetLoading } ] = useMutation( CREATE_ASSET );
    const [ createScormCourse ] = useMutation( CREATE_SCORM_COURSE );
    const [ notifyScormCourse ] = useMutation( NOTIFY_SCORM_COURSE );
    const [ scormCourse, { data: uploadStatus, loading: uploadLoading } ] = useLazyQuery( UPLOAD_STATUS_QUERY, { fetchPolicy: 'network-only' } );
    const [ errorFileLoading, setErrorFileLoading ] = useState( false );
    const [ uploadingScormId, setUploadingScormId ] = useState();

    const handleUpload = async ( file ) => {    
        setErrorFileLoading( false );

        // Large file upload for zip files
        if ( file.type === 'application/zip' || file.type === 'application/x-zip-compressed' ) {            
            setLargeFileLoading( true );
            
            //////////////////////////////////////////
            // Step 1: createScormCourse
            //////////////////////////////////////////

            let scormId;
            let scormSignedUrl;
 
            let variables = {
                input: {
                    name: file.name
                }
            };  
            
            try {
                const response = await createScormCourse( { variables } );
                scormId = response?.data?.result?.id;
                setUploadingScormId( scormId );
                scormSignedUrl = response?.data?.result?.signedUrl;
            
                if ( !scormId || !scormSignedUrl ) {
                    console.error( 'ERROR UPLOADING AT: createScormCourse' );
                    setErrorFileLoading( true );
                    setLargeFileLoading( false );
                    return;
                }
            } catch ( e ) {
                console.error( 'ERROR UPLOADING AT: createScormCourse', e );
                setErrorFileLoading( true );
                setLargeFileLoading( false );
                return;
            }

            //////////////////////////////////////////
            // Step 2: PUT zip file
            //////////////////////////////////////////

            try {
                const putResponse = await fetch( scormSignedUrl, {
                    method: 'PUT',
                    body: file
                } );

                if ( !putResponse.ok ) {
                    throw new Error( `Upload failed: ${ putResponse.status }` );
                }
            } catch ( e ) {
                console.error( 'ERROR PUTing SCORM file: ', e );
            }
            
            //////////////////////////////////////////
            // Step 3: notifyScormCourse
            //////////////////////////////////////////

            variables = {
                input: {
                    id: scormId
                }
            };  
             
            try {
                const resp = await notifyScormCourse( { variables } );
                const resultStatus = resp?.data?.result?.status; 
                 
                if ( resultStatus !== 'PROCESSING' ) {
                    console.error( 'ERROR UPLOADING AT: notifyScormCourse' );
                    setErrorFileLoading( true );
                    setLargeFileLoading( false );
                    return;
                }
            } catch ( e ) {
                console.error( 'ERROR UPLOADING AT: notifyScormCourse', e );
                setErrorFileLoading( true );
                setLargeFileLoading( false );
                return;
            }

            //////////////////////////////////////////
            // Step 4: scormCourse
            //////////////////////////////////////////
            
            variables = {
                scormCourseID: scormId
            };
            
            try {
                scormCourse( { variables } );
            } catch ( e ) {
                console.error( 'ERROR UPLOADING AT: scormCourse', e );
                setErrorFileLoading( true );
                setLargeFileLoading( false );
                return;
            }
        } else {

            // this is legacy uploading which we will need later for other file types
            const variables = {
                'input': {
                    'id': uuidv4(),
                    'name': file.name,
                    'type': type.toUpperCase(), 
                    'file': file
                }
            }; 
        
            const response = await createAsset( { variables } );
            const success = !( response?.error || response?.errors && response?.errors?.length > 0 );
            const newAsset = response?.data?.result;

            if ( success && newAsset ) {
                onChange( [ ...assetList, newAsset ] );
            }
        }    
    };
    
    useEffect( () => {
        const node = uploadStatus?.scormCourse?.node;

        const createLargeFileAsset = async ( name, scormCourseId ) => {
            const variables = {
                'input': {
                    'id': uuidv4(),
                    'name': name,
                    'type': 'SCORM',
                    'scormCourseId': scormCourseId 
                }
            };

            try {
                const response = await createAsset( { variables } );
                const success = !( response?.error || response?.errors && response?.errors?.length > 0 );
                const newAsset = response?.data?.result;

                if ( success && newAsset ) {
                    onChange( [ ...assetList, newAsset ] );
                }   

                setLargeFileLoading( false );
            } catch ( e ) {
                console.error( 'ERROR UPLOADING AT: createLargeFileAsset', e );
                setErrorFileLoading( true );
                setLargeFileLoading( false );
                return;
            }
        };

        if ( node?.status === 'COMPLETED' ) {
            createLargeFileAsset( node.name, node.id ) ;
            return;
        } 

        if ( node?.status === 'PROCESSING' && uploadLoading === false ) {
            setTimeout( () => {
                const variables = {
                    scormCourseID: uploadingScormId
                };
                
                try {
                    scormCourse( { variables } );
                } catch ( e ) {
                    console.error( 'ERROR UPLOADING AT: scormCourse', e );
                    setErrorFileLoading( true );
                    setLargeFileLoading( false );
                    return;
                }
            }, 2000 );
        }
    }, [ uploadStatus, uploadLoading ] );
    
    /**
     * Handles the deletion of the file/asset with the matching ID.
     * 
     * @param { string } id - The ID of the asset to delete.
     */
    const handleDelete = async ( assetId ) => {
        if ( assetId ) { 
            // const response = await deleteAsset( { variables: { assetID: assetId } } );
            const success = true; //!( response?.error || response?.errors && response?.errors?.length > 0 );

            const indexToDelete = assetList.map( ( asset ) => asset?.id ).indexOf( assetId );
            if ( success && indexToDelete >= 0 ) {
                let newValues = [ ...assetList ];
                newValues.splice( indexToDelete, 1 );
                onChange( newValues );
            }
        } else {
            console.error( `Attempting to delete an asset with invalid id '${ assetId }'! Ensure you are providing a valid id to your mutation.` );
        }
    };
 
    const loading = ( createAssetLoading || largeFileLoading ); 

    return (
        <div className="asset-upload">

            { !errorFileLoading &&
                <>
                    <div>
                        { assetList.map( ( asset ) => {
                            return (
                                <AssetPreview
                                    key={ asset?.id }
                                    name={ asset?.name }
                                    type={ asset?.mimetype }
                                    onRemove={ () => handleDelete( asset?.id ) }
                                />
                            );
                        } ) }
                    </div>

                    { ( assetList.length < maxAssets && canCreate ) &&
                        <UploadButton onAdd={ handleUpload } accept={ accept }>
                            Drag and drop or click to choose file to upload
                        </UploadButton>
                    }

                    { ( loading ) &&
                        <Loading />
                    }
                </>
            }

            { errorFileLoading &&
                <Alert message="Error uploading file" type="error" /> 
            }
        </div>
    );
};

export default AssetUpload;
